import { APP_VERSION, APP_BUILD_CHANNEL } from '@/constants/version';
import { Link } from 'react-router';
import { UserMenu } from './UserMenu';
import { ResumeCartChip } from './ResumeCartChip';
import { PopOutButton } from './PopOutButton';
import { ActiveBranchChip } from './ActiveBranchChip';
import { NotificationBell } from '@/modules/_shared/NotificationBell';
import { HelpButton } from './HelpButton';
import pcbLogoUrl from '@/assets/phelps-co-bank-logo-white.svg';

export interface TopBarProps {
  /**
   * Fires when the user clicks the search button or presses Cmd/Ctrl+K.
   * The command palette implementation lives in the shell so keyboard
   * shortcuts work from any module. TopBar just raises the intent.
   */
  onOpenPalette?: () => void;
}

/**
 * Compact topbar — 40px tall. Teams already provides the app's title +
 * user identity in its surrounding chrome.
 *
 * The "Pop out" affordance uses Teams Stageview (stageView.open with
 * openMode='popout') — the official multi-window API. Desktop-only; the
 * button hides itself when already in a pop-out window. See PopOutButton.
 */
export function TopBar({ onOpenPalette }: TopBarProps): JSX.Element {
  const envLabel = APP_BUILD_CHANNEL === 'prod' ? 'PROD' : APP_BUILD_CHANNEL === 'uat' ? 'UAT' : 'DEV';

  return (
    <header className="shell__topbar" role="banner">
      <div className="shell__topbar-brand">
        {/* The logo is the universal way home (Bill 2026-09-11). */}
        <Link to="/" className="shell__topbar-brand-home" aria-label="Home" title="Home">
          <img src={pcbLogoUrl} alt="PCB" className="shell__topbar-brand-logo" />
        </Link>
        <span className="shell__topbar-env-chip" title={`v${APP_VERSION}`}>
          {envLabel} v{APP_VERSION}
        </span>
      </div>

      {/* PILOT 2026-09-11 (Bill): the header search / command palette is
          OFF. It promised "customers, accounts, modules" and delivered a
          partially wired palette - the kind of surface that looks like a
          shortcut and turns into a support call. Customer lookup is the Find
          Customer action on Home, which works. The freed width goes to the
          branch badge and the Resume chip, both of which now carry real names.
          The trigger is retained below as a spacer-free no-op; restore the
          button when the palette is wired end to end. */}
      <div className="shell__topbar-palette-spacer" aria-hidden />

      <div className="shell__topbar-actions">
        <PopOutButton />
        <ActiveBranchChip />
        <ResumeCartChip />
        <HelpButton />
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}
